import { provideAppInitializer, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { devLog, devError } from '@app/core/utils/dev-logger';
import { PwaUpdateService } from '@app/core/services/pwa-update.service';

/**
 * Application Initializer
 *
 * Only loads global, non-authenticated data to avoid circular dependencies.
 * User-specific data (projects, templates, taxonomies, system keys, entity types)
 * is loaded in SessionInitService after login.
 *
 * Modern approach using provideAppInitializer (Angular 20+)
 */
export function initializeApp(translate: TranslateService): Observable<any> {
  devLog('🚀 Starting application initialization...');

  // Get the browser language
  const browserLang = navigator.language;
  const defaultLang = browserLang.startsWith('es') ? 'es' : 'en';

  // Set the default language
  translate.setDefaultLang(defaultLang);

  // Load translations - this is the ONLY thing that should block app initialization
  // All user-specific data (projects, templates, taxonomies, system keys, entity types)
  // is loaded in SessionInitService after login to avoid circular dependencies
  return translate.use(defaultLang).pipe(
    tap(() => {
      devLog(`✅ Translations loaded for language: ${defaultLang}`);
      devLog('🎉 Application initialization completed');
    }),
    catchError((error) => {
      devError('❌ Failed to load translations:', error);
      // Return an empty observable to prevent app from failing
      return of(null);
    })
  );
}

/**
 * Initialize PWA update checking
 * Non-blocking - runs after app initialization
 */
export function initializePwaUpdates() {
  const pwaUpdate = inject(PwaUpdateService);
  pwaUpdate.initialize();
}

/**
 * Provider for app initialization using modern Angular 20+ approach
 */
export function provideAppInit() {
  return provideAppInitializer(() => {
    const translate = inject(TranslateService);
    return initializeApp(translate);
  });
}

/**
 * Provider for PWA update initialization
 * Separate from main initialization to avoid blocking
 */
export function providePwaUpdateInit() {
  return provideAppInitializer(() => {
    initializePwaUpdates();
  });
}
